import { gql } from '@apollo/client';
import { useParams } from 'react-router';
import {
  getMultiYearOperationsMetrics,
  getOperationsMetrics,
} from '../constants/operationsMetrics';
import { CropDisplayFragment, OperationsEconFragment } from '../queries';
import { avg } from '../utils/aggregators';
import { colorForCrop } from '../utils/colors';
import { asCurrency } from '../utils/formatters';
import OperationsViewTemplate from './OperationsViewTemplate';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import { useSubsidySwitchContext } from '../contexts/SubsidySwitchContext';
import {
  useDataTransforms,
  useUserCanEditFarm,
  useUserRole,
} from '../utils/hooks';
import {
  getOperationsSummary,
  occursDuringYearFilter,
  toAggregatesMap,
} from '../utils/operations';
import { useFilterContext } from '../contexts/FilterContext';
import { MULTI_YEAR } from '../constants/years';

const ALL_CROPS = 'All Crops';
const QUERY_NAME = 'OperationsEconQuery';
const FRAGMENT_NAME = 'OperationsViewFragment';

const OperationsFragment = gql`
  ${CropDisplayFragment}
  ${OperationsEconFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    ...OperationsEconFragment
    cropAggregatesUnsubsidized: cropAggregates(
      applyMargin: $applyMargin
      applySubsidy: false
    ) {
      operationAggregates {
        operation {
          id
        }
        totalCostPerTree
      }
    }
  }
`;

const getSummaryMetrics = ({ cropAggregates }) =>
  [{ crop: { name: ALL_CROPS, isAggregate: true } }, ...cropAggregates].map(
    ({ crop }) => ({
      displayName: crop.name,
      accessor: crop.name,
      isAggregate: crop.isAggregate,
      aggregator: avg,
      color: colorForCrop(crop),
      defaultShown: true,
      format: asCurrency,
      info: '(Avg)',
      yAxisId: 'currency',
    }),
  );

const toSummaryData = getOperationsSummary('totalCostByYear', ALL_CROPS);

// Merge the unsubsidized data into the subsidized data
const mergeUnsubsidizedCropAggregates = (data) => {
  const unsubsidizedDataMap = toAggregatesMap(data.cropAggregatesUnsubsidized);
  return {
    ...data,
    cropAggregates: data.cropAggregates.map((crop) => ({
      ...crop,
      operationAggregates: crop.operationAggregates.map(
        (operationAggregate) => {
          const unsubsidizedOperationAggregate =
            unsubsidizedDataMap[operationAggregate.operation.id];
          return {
            ...operationAggregate,
            unsubsidizedTotalCostPerTree:
              unsubsidizedOperationAggregate?.totalCostPerTree,
          };
        },
      ),
    })),
  };
};

const OperationsView = () => {
  const { farmId } = useParams();
  const { shouldShowSubsidies } = useSubsidySwitchContext();
  const { isSuperadmin } = useUserRole();
  const { msg, data } = useFarmOrSectionGroupData(
    OperationsFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
    [
      { name: 'applyBuffer', typeString: 'Boolean', value: true },
      {
        name: 'applyMargin',
        typeString: 'Boolean',
        value: true,
      },
      {
        name: 'applySubsidy',
        typeString: 'Boolean',
        value: shouldShowSubsidies,
      },
      {
        name: 'assignment',
        typeString: 'String',
        value: null,
      },
    ],
  );

  const {
    state: { year },
  } = useFilterContext();
  // Merge cropAggregatesUnsubsidized with cropAggregates
  const [mergedData] = useDataTransforms(data, [
    mergeUnsubsidizedCropAggregates,
  ]);

  const showLink = useUserCanEditFarm(farmId);
  const showSubsidyPerTree = shouldShowSubsidies && isSuperadmin;

  return (
    <OperationsViewTemplate
      msg={msg}
      data={mergedData}
      header="Operation Expenses"
      toSummaryData={toSummaryData}
      getSummaryMetrics={getSummaryMetrics}
      metricsForCategory={
        year === MULTI_YEAR
          ? getMultiYearOperationsMetrics(showLink, showSubsidyPerTree)
          : getOperationsMetrics(showLink)
      }
      filterForYear={occursDuringYearFilter}
    />
  );
};

export default OperationsView;
